import { Building2, DoorOpen, Landmark, Ruler, TrainFront, Wifi, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { locale, tx } from '../../i18n';
import { formatPence } from '../../lib/format';
import { byCategory, floorLevelLabel, propertyTypeLabel, tierLabel } from '../../lib/report';
import type { AnalysisDetailResponse, AnalysisSummaryResponse } from '../../types/api';

interface KeyFactsProps {
  summary: AnalysisSummaryResponse;
  detail: AnalysisDetailResponse | null;
}

interface Fact {
  icon: LucideIcon;
  label: string;
  value: string;
}

function metres(value: number): string {
  if (value < 1000) {
    return `${Math.round(value / 10) * 10} m`;
  }
  return `${(value / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 })} km`;
}

/**
 * The row a listing puts under its photo: the handful of facts a reader checks before deciding
 * whether the rest is worth reading. Everything here is repeated in full further down, so a fact
 * that is missing is simply left out rather than shown as a gap.
 */
export default function KeyFacts({ summary, detail }: KeyFactsProps) {
  const interior = summary.homeInterior;
  const facts: Fact[] = [];

  if (interior?.propertyType) {
    facts.push({
      icon: Building2,
      label: tx('Type', 'Tür',
    'Rodzaj',
    'Tipo',
    'Tipo'),
      value: interior.floorLevel
        ? `${propertyTypeLabel(interior.propertyType)}, ${floorLevelLabel(interior.floorLevel)}`
        : propertyTypeLabel(interior.propertyType),
    });
  }
  if (interior?.habitableRooms) {
    facts.push({
      icon: DoorOpen,
      label: tx('Habitable rooms', 'Yaşam odası',
    'Pokoje mieszkalne',
    'Divisões habitáveis',
    'Estancias habitables'),
      value: String(interior.habitableRooms),
    });
  }
  if (interior?.floorAreaSqm) {
    facts.push({
      icon: Ruler,
      label: tx('Floor area', 'Alan',
    'Powierzchnia',
    'Área',
    'Superficie'),
      value: `${Math.round(interior.floorAreaSqm)} m²`,
    });
  }

  const comfort = detail ? byCategory(detail.categories).COMFORT?.metrics : undefined;
  const band = typeof comfort?.band === 'string' ? comfort.band : null;
  if (band) {
    facts.push({ icon: Zap, label: tx('Energy', 'Enerji',
    'Energia',
    'Energia',
    'Energía'), value: `EPC ${band}` });
  }

  const connectivity = detail ? byCategory(detail.categories).CONNECTIVITY?.metrics : undefined;
  const tier = connectivity
    ? tierLabel(
        typeof connectivity.tier === 'string' ? connectivity.tier : null,
        typeof connectivity.tierLabel === 'string' ? connectivity.tierLabel : null,
      )
    : null;
  if (tier) {
    facts.push({ icon: Wifi, label: tx('Broadband', 'İnternet',
    'Internet',
    'Internet',
    'Internet'), value: tier });
  }

  const station = summary.nearbyTransport?.stations?.[0];
  if (station) {
    facts.push({
      icon: TrainFront,
      label: tx('Nearest station', 'En yakın istasyon',
    'Najbliższa stacja',
    'Estação mais próxima',
    'Estación más cercana'),
      value: `${station.name} · ${metres(station.distanceMetres)}`,
    });
  }

  const councilTax = summary.councilTax;
  const common = councilTax?.commonBand
    ? councilTax.bands.find((entry) => entry.band === councilTax.commonBand)
    : undefined;
  if (councilTax?.commonBand && common) {
    facts.push({
      icon: Landmark,
      label: tx(`Council tax, band ${councilTax.commonBand}`, `Council tax, band ${councilTax.commonBand}`,
    `Podatek lokalny, przedział ${councilTax.commonBand}`,
    `Imposto municipal, escalão ${councilTax.commonBand}`,
    `Impuesto municipal, tramo ${councilTax.commonBand}`),
      value: tx(`${formatPence(common.monthlyPence)} a month`, `ayda ${formatPence(common.monthlyPence)}`,
    `${formatPence(common.monthlyPence)} miesięcznie`,
    `${formatPence(common.monthlyPence)} por mês`,
    `${formatPence(common.monthlyPence)} al mes`),
    });
  }

  if (facts.length === 0) {
    return null;
  }

  return (
    <dl className="key-facts" data-tour="key-facts">
      {facts.map((fact) => (
        <div key={fact.label} className="key-fact">
          <fact.icon size={16} aria-hidden="true" className="key-fact__icon" />
          <div className="key-fact__text">
            <dt className="key-fact__label">{fact.label}</dt>
            <dd className="key-fact__value">{fact.value}</dd>
          </div>
        </div>
      ))}
    </dl>
  );
}
